import { getCoverageLineConfig, getCoverageLinesConfig } from '@common/config';
import { ApprovalStatusDetails, SubmissionExtractedData } from '@common/types';
import { isEmpty } from 'lodash';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Button, Divider, Stack, Tooltip, Typography } from '@common-components';
import { ApprovalStatus, CoverageLine, DuplicationResult, StateCode } from 'enums';
import { useBoolean, useFormProvider, useMutateCarrierSubmission, useScrollSections } from 'hooks';
import { messages } from 'i18n';
import { CarrierSubmission, CarrierSubmissionUpdate, Suggestion } from 'types';
import FormDatePicker from 'components/hookFormComponents/FormDatePicker';
import { FormMultiSelectAutocomplete } from 'components/hookFormComponents/FormMultiSelectAutocomplete';
import FormTextField from 'components/hookFormComponents/FormTextField';
import { SuggestionProps } from 'components/hookFormComponents/types';
import { FormWithStepper } from 'broker/components/common/FormWithStepper';
import IndustryAutoComplete from 'broker/components/common/IndustryAutoComplete';
import { AIReason } from 'broker/pages/SubmissionWorkspacePage/components/AIGeneratedContent/AIReason';
import { useSendPromptReport } from 'broker/utils/use-send-prompt-report';
import ApprovalReasonDialog from './ApprovalReasonDialog';
import CarrierSubmissionAddressFields from './CarrierSubmissionAddressFields';
import ClearanceStatusSection from './ClearanceStatusSection';
import ComingSoonSection from './ComingSoonSection';
import { useAllFieldsFilled } from './useAllFieldsFilled';
import {
  buildDefaultValues,
  carrierSubmissionFormSchema,
  CarrierSubmissionFormValues,
  useCarrierSubmissionExtractedData,
} from './useCarrierSubmissionForm';

const {
  sections: { basicInformation, clearanceStatus, complianceChecks, triageCompleteness },
  fields,
  save,
  approve,
  flag,
  reject,
  flagDialog: flagDialogMessages,
  rejectDialog: rejectDialogMessages,
  approvalDisabledTooltip,
} = messages.carrierSubmissionWorkspace;

const sections = [
  { id: 'basic-information', label: basicInformation },
  { id: 'compliance-checks', label: complianceChecks },
  { id: 'clearance-status', label: clearanceStatus },
  { id: 'triage-completeness', label: triageCompleteness },
];

const coverageLineOptions = Object.entries(getCoverageLinesConfig()).map(([value]) => ({
  label: getCoverageLineConfig(value as CoverageLine).text,
  value,
}));

export interface CarrierSubmissionDetailsFormHandle {
  validateAndSave: () => Promise<boolean>;
}

interface CarrierSubmissionDetailsFormProps {
  carrierSubmission: CarrierSubmission;
  submissionExtractedData?: SubmissionExtractedData;
  onSave: () => void;
  onDirtyChange: (isDirty: boolean) => void;
}

const CarrierSubmissionDetailsForm = forwardRef<CarrierSubmissionDetailsFormHandle, CarrierSubmissionDetailsFormProps>(
  ({ carrierSubmission, submissionExtractedData, onSave, onDirtyChange }, ref) => {
    const { updateCarrierSubmission } = useMutateCarrierSubmission();
    const sendPromptReport = useSendPromptReport();
    const suggestedValues = useCarrierSubmissionExtractedData(submissionExtractedData);
    const currentApprovalStatus = carrierSubmission.approvalStatus?.status;

    const defaultValues = useMemo(() => buildDefaultValues(carrierSubmission), [carrierSubmission]);

    // Derive first visit from persisted data, matching EditSubmissionContent's firstVisit pattern.
    // Once coverageLines are saved, subsequent visits won't re-apply suggestions.
    const isFirstVisit = useMemo(() => isEmpty(carrierSubmission.submission.coverageLines), [carrierSubmission]);

    const [isDirty, setIsDirty] = useState(false);

    const { methods, appliedSuggestions } = useFormProvider<CarrierSubmissionFormValues>({
      schema: carrierSubmissionFormSchema,
      defaultValues,
      suggestedValues,
      applySuggestionsOnEmptyFields: isFirstVisit,
      setIsDirty,
    });

    useEffect(() => {
      onDirtyChange(isDirty);
    }, [isDirty, onDirtyChange]);

    const allFieldsFilled = useAllFieldsFilled(methods);
    const [isFlagDialogOpen, { on: openFlagDialog, off: closeFlagDialog }] = useBoolean();
    const [isRejectDialogOpen, { on: openRejectDialog, off: closeRejectDialog }] = useBoolean();

    // Stepper state — mirrors useDynamicFormPersistentState pattern
    const [activeSection, setActiveSection] = useState<string | undefined>(sections[0].id);
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const scrollableDivRef = useRef<HTMLDivElement | null>(null);
    const isScrollingProgrammatically = useRef(false);

    // Wrap setActiveSection to ignore scroll-spy updates during programmatic scrolling
    const scrollSetActiveSection = useCallback((section: string | undefined) => {
      if (!isScrollingProgrammatically.current) {
        setActiveSection(section);
      }
    }, []);

    useScrollSections({
      scrollableDivRef,
      sectionRefs,
      activeSection,
      setActiveSection: scrollSetActiveSection,
    });

    const onSetActiveSection = useCallback((section?: string) => {
      isScrollingProgrammatically.current = true;
      setActiveSection(section);
      if (section && sectionRefs.current[section]) {
        sectionRefs.current[section]?.scrollIntoView({ behavior: 'smooth' });
      }
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 500);
    }, []);

    // Build suggestion overlays per field
    const coverageLinesSuggestion = useMemo(
      () =>
        Array.isArray(appliedSuggestions.coverageLines?.value)
          ? {
              value: (appliedSuggestions.coverageLines.value as CoverageLine[]).map(
                (cl) => getCoverageLineConfig(cl).text,
              ),
              reason: <AIReason suggestion={appliedSuggestions.coverageLines as Suggestion} />,
              confidence: appliedSuggestions.coverageLines?.confidence,
            }
          : undefined,
      [appliedSuggestions],
    );

    const resolvedSuggestions = useMemo(() => {
      const resolved: Record<string, SuggestionProps> = {};
      Object.entries(appliedSuggestions).forEach(([key, suggestion]) => {
        if (key === 'coverageLines' || key === 'insuredMailingAddress') {
          return; // handled separately above
        }
        resolved[key] = {
          value: suggestion.value,
          reason: <AIReason suggestion={suggestion as Suggestion} />,
          confidence: suggestion.confidence,
        };
      });
      return resolved;
    }, [appliedSuggestions]);

    const submitForm = async (approvalStatus?: ApprovalStatusDetails) => {
      const formValues = methods.getValues();
      const payload: CarrierSubmissionUpdate = {
        agentName: formValues.agentName,
        insuredName: formValues.insuredName,
        industry: formValues.industry
          ? {
              heraldId: formValues.industry.heraldId,
              naicsCode: formValues.industry.naicsCode,
              description: formValues.industry.description,
            }
          : undefined,
        insuredMailingAddress: !isEmpty(formValues.insuredMailingAddress?.line1)
          ? {
              formattedAddress: [
                formValues.insuredMailingAddress.line1,
                formValues.insuredMailingAddress.city,
                formValues.insuredMailingAddress.state,
                formValues.insuredMailingAddress.postalCode,
              ]
                .filter(Boolean)
                .join(', '),
              line1: formValues.insuredMailingAddress.line1,
              line2: formValues.insuredMailingAddress.line2,
              city: formValues.insuredMailingAddress.city,
              state: formValues.insuredMailingAddress.state as StateCode,
              postalCode: formValues.insuredMailingAddress.postalCode,
              country: formValues.insuredMailingAddress.country,
            }
          : undefined,
        coverageLines: formValues.coverageLines as CoverageLine[],
        dueDate: formValues.dueDate ? new Date(formValues.dueDate) : undefined,
        sic: formValues.sic,
        natureOfBusiness: formValues.natureOfBusiness,
        fein: formValues.fein,
        ...(!isEmpty(formValues.businessEmail) ? { businessEmail: formValues.businessEmail } : {}),
        ...(approvalStatus ? { approvalStatus } : {}),
        ...(carrierSubmission.clearanceDetails && formValues.duplicationStatus
          ? {
              clearanceDetails: {
                duplication: {
                  ...carrierSubmission.clearanceDetails.duplication,
                  status: formValues.duplicationStatus as DuplicationResult,
                },
              },
            }
          : {}),
      };
      await updateCarrierSubmission.mutateAsync({
        id: carrierSubmission.id,
        data: payload,
      });
      if (suggestedValues) {
        sendPromptReport({
          formState: methods.getValues(),
          suggestedValueProps: suggestedValues,
          submissionId: carrierSubmission.submission.id,
          entityId: carrierSubmission.id,
        });
      }
    };

    const handleSave = () => {
      methods.handleSubmit(async () => {
        await submitForm();
        onSave();
      })();
    };

    const handleApprove = () => {
      methods.handleSubmit(async () => {
        await submitForm({ status: ApprovalStatus.Approved, reason: '' });
        onSave();
      })();
    };

    const handleFlagConfirm = async (reason: string) => {
      await methods.handleSubmit(async () => {
        try {
          await submitForm({ status: ApprovalStatus.Flagged, reason });
          onSave();
          closeFlagDialog();
        } catch {
          // Keep dialog open on error
        }
      })();
    };

    const handleRejectConfirm = async (reason: string) => {
      await methods.handleSubmit(async () => {
        try {
          await submitForm({ status: ApprovalStatus.Rejected, reason });
          onSave();
          closeRejectDialog();
        } catch {
          // Keep dialog open on error
        }
      })();
    };

    const validateAndSave = async (): Promise<boolean> => {
      const isValid = await methods.trigger();
      if (!isValid) {
        return false;
      }
      try {
        await submitForm();
        return true;
      } catch {
        return false;
      }
    };

    useImperativeHandle(ref, () => ({
      validateAndSave,
    }));

    // Access formState.errors during render to subscribe to error changes in RHF's proxy.
    // Without this, memoized FormTextField children won't re-render to display validation errors.
    // This mirrors how DynamicFormLayout subscribes via useGetSectionsIndicators.
    const hasErrors = Object.keys(methods.formState.errors).length > 0;

    const hasClearanceData = !!carrierSubmission.clearanceDetails;

    const sectionList = useMemo(
      () =>
        sections.map((section) => {
          if (section.id === 'basic-information') {
            return {
              ...section,
              hasError: hasErrors,
            };
          }
          if (section.id === 'clearance-status') {
            return {
              ...section,
              isCompleted: hasClearanceData,
            };
          }
          return {
            ...section,
            isCompleted: true,
          };
        }),
      [hasErrors, hasClearanceData],
    );

    return (
      <FormProvider {...methods}>
        <Stack flex={1} minHeight={0} pt={4}>
          <FormWithStepper
            sectionProps={{
              activeSection,
              setActiveSection: onSetActiveSection,
              sectionList,
            }}
            ref={scrollableDivRef}
            withPadding
          >
            <Stack divider={<Divider sx={{ mt: 2, mb: 5 }} />}>
              <Stack
                ref={(el) => {
                  sectionRefs.current['basic-information'] = el as HTMLDivElement | null;
                }}
              >
                <Typography variant="subtitle2" mb={2}>
                  {basicInformation}
                </Typography>
                <FormTextField
                  name="insuredName"
                  id="insuredName"
                  label={fields.insuredName}
                  suggestion={resolvedSuggestions.insuredName}
                />
                <FormTextField
                  name="agentName"
                  id="agentName"
                  label={fields.agentName}
                  suggestion={resolvedSuggestions.agentName}
                />
                <FormMultiSelectAutocomplete
                  name="coverageLines"
                  label={fields.lineOfBusiness}
                  options={coverageLineOptions}
                  suggestion={coverageLinesSuggestion}
                />
                <FormDatePicker
                  name="dueDate"
                  id="dueDate"
                  label={fields.effectiveDate}
                  suggestion={resolvedSuggestions.dueDate}
                />
                <CarrierSubmissionAddressFields
                  suggestion={appliedSuggestions.insuredMailingAddress as Suggestion | undefined}
                />
                <FormTextField
                  name="businessEmail"
                  id="businessEmail"
                  label={fields.businessEmail}
                  suggestion={resolvedSuggestions.businessEmail}
                  optional
                />
                <IndustryAutoComplete
                  fieldProps={{ name: 'industry', label: fields.industry }}
                  suggestion={resolvedSuggestions.industry}
                />
                <FormTextField
                  name="natureOfBusiness"
                  id="natureOfBusiness"
                  label={fields.natureOfBusiness}
                  suggestion={resolvedSuggestions.natureOfBusiness}
                />
                <FormTextField name="sic" id="sic" label={fields.sicCode} suggestion={resolvedSuggestions.sic} />
                <FormTextField name="fein" id="fein" label={fields.taxIdFein} suggestion={resolvedSuggestions.fein} />
              </Stack>

              <Stack
                ref={(el) => {
                  sectionRefs.current['compliance-checks'] = el as HTMLDivElement | null;
                }}
              >
                <Typography variant="subtitle2" mb={2}>
                  {complianceChecks}
                </Typography>
                <ComingSoonSection />
              </Stack>

              <Stack
                ref={(el) => {
                  sectionRefs.current['clearance-status'] = el as HTMLDivElement | null;
                }}
              >
                <Typography variant="subtitle2" mb={2}>
                  {clearanceStatus}
                </Typography>
                <ClearanceStatusSection carrierSubmission={carrierSubmission} />
              </Stack>

              <Stack
                ref={(el) => {
                  sectionRefs.current['triage-completeness'] = el as HTMLDivElement | null;
                }}
              >
                <Typography variant="subtitle2" mb={2}>
                  {triageCompleteness}
                </Typography>
                <ComingSoonSection />
              </Stack>
            </Stack>
          </FormWithStepper>
          <Stack px={2} py={1} borderTop={1} borderColor="divider">
            <Stack direction="row" gap={1} alignItems="center">
              <Stack flex={1} direction="row" gap={1} alignItems="center">
                <Button variant="contained" onClick={handleSave} disabled={methods.formState.isSubmitting}>
                  {save}
                </Button>
              </Stack>
              <Stack direction="row" gap={1} alignItems="center">
                <Tooltip tooltipContent={approvalDisabledTooltip} enabled={!allFieldsFilled}>
                  <span>
                    <Button
                      variant={currentApprovalStatus === ApprovalStatus.Approved ? 'contained' : 'outlined'}
                      color="success"
                      onClick={handleApprove}
                      disabled={!allFieldsFilled || methods.formState.isSubmitting}
                    >
                      {approve}
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant={currentApprovalStatus === ApprovalStatus.Flagged ? 'contained' : 'outlined'}
                  color="warning"
                  onClick={openFlagDialog}
                  disabled={methods.formState.isSubmitting}
                >
                  {flag}
                </Button>
                <Button
                  variant={currentApprovalStatus === ApprovalStatus.Rejected ? 'contained' : 'outlined'}
                  color="error"
                  onClick={openRejectDialog}
                  disabled={methods.formState.isSubmitting}
                >
                  {reject}
                </Button>
              </Stack>
            </Stack>
          </Stack>

          <ApprovalReasonDialog
            isOpen={isFlagDialogOpen}
            onCancel={closeFlagDialog}
            onConfirm={handleFlagConfirm}
            title={flagDialogMessages.title}
            subtitle={flagDialogMessages.subtitle}
            placeholder={flagDialogMessages.placeholder}
            confirmLabel={flagDialogMessages.confirm}
            isSubmitting={methods.formState.isSubmitting}
            defaultReason={
              currentApprovalStatus === ApprovalStatus.Flagged ? carrierSubmission.approvalStatus?.reason ?? '' : ''
            }
          />
          <ApprovalReasonDialog
            isOpen={isRejectDialogOpen}
            onCancel={closeRejectDialog}
            onConfirm={handleRejectConfirm}
            title={rejectDialogMessages.title}
            subtitle={rejectDialogMessages.subtitle}
            placeholder={rejectDialogMessages.placeholder}
            confirmLabel={rejectDialogMessages.confirm}
            isSubmitting={methods.formState.isSubmitting}
            defaultReason={
              currentApprovalStatus === ApprovalStatus.Rejected ? carrierSubmission.approvalStatus?.reason ?? '' : ''
            }
          />
        </Stack>
      </FormProvider>
    );
  },
);

CarrierSubmissionDetailsForm.displayName = 'CarrierSubmissionDetailsForm';

export default CarrierSubmissionDetailsForm;
